// libs
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  ButtonBase,
  SxProps,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AliceCarousel from 'react-alice-carousel';
import 'react-alice-carousel/lib/alice-carousel.css';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// styles
import styles, {
  getCarouselStyle,
  nextButtonStyles,
  prevButtonStyles
} from './styles';
import useClientOnly from '../../utils/customHooks/useClientOnly';

export interface LibCarouselLocalLifeProps {
  itemData?: JSX.Element[];
  idKeys: string[];
  isLocalLifeDetailPage?: boolean;
  isPropertyDetailPage?: boolean;
  isInteriorDesignPage?: boolean;
}

function LibCarouselLocalLife({
  itemData = [],
  idKeys,
  isLocalLifeDetailPage = false,
  isPropertyDetailPage = false,
  isInteriorDesignPage = false
}: LibCarouselLocalLifeProps) {
  const { hasMounted } = useClientOnly();
  const isDesktop = useMediaQuery('(min-width:56.25rem)');
  // getting screensizes for carousel dots
  const theme = useTheme();
  const isSm = useMediaQuery('(min-width:0px) and (max-width:850.99px)');
  const isMdSm = useMediaQuery('(min-width:851px) and (max-width:1024.99px)');
  const isMd = useMediaQuery('(min-width:1025px) and (max-width:1534.99px)');
  const isLg = useMediaQuery('(min-width:1535px)');

  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [activeIndex, setActiveIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const carouselRef = useRef<AliceCarousel>(null); // Ref for the carousel component

  const [carouselVisibleItemsCount, setCarouselVisibleItemsCount] = useState(
    () => {
      if (isSm) return 1;
      if (isMdSm) return 2;

      if (isMd) return 3;
      if (isLg) return 4;

      return 4;
    }
  );

  const handleDragStart = (e) => {
    e.preventDefault();
  };

  useEffect(() => {
    setActiveIndex(0);
    setCarouselVisibleItemsCount(() => {
      if (
        (isMd || isLg || isTablet) &&
        (isPropertyDetailPage || isInteriorDesignPage)
      )
        return isInteriorDesignPage ? 1 : 2;
      if (isSm) return 1;
      if (isMdSm) return 2;

      if (isMd) return 3;
      if (isLg) return 4;

      return 4;
    });
  }, [isSm, isMd, isLg, isTablet]);

  const handleClickNextButton = (event) => {
    event.stopPropagation(); // Prevent click event propagation
    setIsDragging(true); // Set dragging state to true
    setTimeout(() => setIsDragging(false), 500); // Reset dragging state after 500ms
    setActiveIndex((p) => {
      let itemsToScroll = 1;

      if (isLocalLifeDetailPage) {
        itemsToScroll = carouselVisibleItemsCount;
      }

      const nextNo = p + itemsToScroll;

      if (nextNo > itemData.length) {
        carouselRef?.current?.slideTo(0);

        return 0;
      }

      carouselRef?.current?.slideTo(nextNo);

      return nextNo;
    });
  };

  const handleClickPrevButton = (event) => {
    event.stopPropagation(); // Prevent click event propagation
    setIsDragging(true); // Set dragging state to true
    setTimeout(() => setIsDragging(false), 500); // Reset dragging state after 500ms
    setActiveIndex((p) => {
      let itemsToScroll = 1;

      if (isLocalLifeDetailPage) {
        itemsToScroll = carouselVisibleItemsCount;
      }

      const nextNo = p - itemsToScroll;

      if (nextNo < 0) {
        const lastDot =
          Math.ceil(itemData.length / itemsToScroll) * itemsToScroll -
          itemsToScroll;

        carouselRef?.current?.slideTo(lastDot);

        return lastDot;
      }

      carouselRef?.current?.slideTo(nextNo);

      return nextNo;
    });
  };

  const renderNextButton = () => (
    <ButtonBase
      onClick={handleClickNextButton}
      disableRipple
      id="nextBtn"
      aria-label="nextBtn"
      sx={nextButtonStyles({
        activeIndex,
        isSmallScreen: isSm,
        carouselVisibleItemsCount,
        itemData,
        isInteriorDesignPage,
        isPropertyDetailPage
      })}
    >
      <ArrowForwardIosIcon />
    </ButtonBase>
  );

  const renderPrevButton = () => (
    <ButtonBase
      disableRipple
      onClick={handleClickPrevButton}
      id="prevBtn"
      aria-label="nextBtn"
      sx={prevButtonStyles({
        activeIndex,
        itemData,
        isSmallScreen: isSm,
        isInteriorDesignPage,
        isPropertyDetailPage
      })}
    >
      <ArrowBackIosIcon />
    </ButtonBase>
  );

  const handleDotClick = (index: number) => {
    setActiveIndex(index);

    carouselRef?.current?.slideTo(index); // Smoothly transition to the clicked index
  };

  const handleSlideChange = ({ item }) => {
    if (!isDesktop && !isTablet) {
      setActiveIndex(item);
    }
  };

  return (
    <Box
      sx={
        [
          getCarouselStyle({
            isInteriorDesignPage,
            isTablet,
            activeIndex,
            itemData,
            isLocalLifeDetailPage,
            isPropertyDetailPage
          }),
          styles.carouselBody,
          isPropertyDetailPage && styles.carouselMarginBottom,
          !hasMounted && styles.ssrMarkup
        ] as SxProps
      }
    >
      <AliceCarousel
        activeIndex={activeIndex}
        autoWidth
        // need to review onSlideChanged, causing carousel issue on slide
        // onSlideChanged={(e) => onSlideChanged(e.item)}
        onSlideChanged={handleSlideChange}
        touchTracking
        mouseTracking
        disableDotsControls
        disableButtonsControls={
          !(
            (isLocalLifeDetailPage ||
              isPropertyDetailPage ||
              isInteriorDesignPage) &&
            (isDesktop || isTablet)
          ) || Math.ceil(itemData.length / carouselVisibleItemsCount) <= 1
        }
        paddingRight={isDesktop || (isTablet && isInteriorDesignPage) ? 40 : 0}
        items={itemData?.map((elem: JSX.Element, idx: number) => (
          <Box
            key={idKeys[idx]}
            onDragStart={handleDragStart}
            sx={{
              width: '21rem',
              pointerEvents: isDragging ? 'none' : 'auto',
              height: isInteriorDesignPage ? '18.5rem' : 'auto',
              ...styles.elementWidthControl
            }}
          >
            {/* {elem()} */}
            {typeof elem === 'object' ? elem : elem()}
          </Box>
        ))}
        renderPrevButton={
          (((isLocalLifeDetailPage || isPropertyDetailPage) && isDesktop) ||
            isInteriorDesignPage) &&
          renderPrevButton
        }
        renderNextButton={
          (((isLocalLifeDetailPage || isPropertyDetailPage) && isDesktop) ||
            isInteriorDesignPage) &&
          renderNextButton
        }
        ref={carouselRef}
        ssrSilentMode
      />
      {isLocalLifeDetailPage ? (
        <Box sx={styles.dotsBox}>
          {Array.from({
            length: Math.ceil(itemData.length / carouselVisibleItemsCount)
          }).map((_, groupIndex) => (
            <Box
              key={groupIndex}
              onClick={() => {
                handleDotClick(groupIndex * carouselVisibleItemsCount);
              }}
              sx={{
                ...styles.localLifeDots,
                opacity:
                  activeIndex >= groupIndex * carouselVisibleItemsCount &&
                  activeIndex < (groupIndex + 1) * carouselVisibleItemsCount
                    ? 1
                    : 0.2
              }}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

export const CarouselLocalLife = React.memo(LibCarouselLocalLife);

export default CarouselLocalLife;
